import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Bookmark, Check, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API, getToken } from "@/lib/api";
import { RENTAL } from "@/constants/testIds";

const buildBookmarkletJs = (apiUrl, token) => {
  const endpoint = `${apiUrl}/rentals/parse_capture`;
  // Chrome + React (Next.js) intercept javascript: URLs during the click event.
  // Wrap work in setTimeout(...,0) + void(0) so the click handler returns
  // immediately and React's router never observes a javascript: URL.
  // The session token is embedded because the bookmarklet runs on a foreign
  // origin and can't read our httpOnly cookie or localStorage.
  const fn = `setTimeout(function(){
    var API=${JSON.stringify(endpoint)};
    var TOKEN=${JSON.stringify(token || "")};
    var u=location.href;
    var html=document.documentElement.outerHTML;
    if(html.length>800000)html=html.slice(0,800000);
    var img="";
    var og=document.querySelector('meta[property="og:image"]');
    if(og)img=og.content;
    if(!img){
      var bigs=Array.from(document.images).filter(function(i){return i.naturalWidth>300});
      if(bigs.length)img=bigs[0].src;
    }
    var t=document.createElement('div');
    t.textContent='LeaseDeck: snagging…';
    t.style.cssText='position:fixed;bottom:20px;right:20px;background:#4A5D4E;color:#F9F8F6;padding:14px 22px;border-radius:14px;font-family:system-ui,sans-serif;font-size:14px;z-index:2147483647;box-shadow:0 12px 32px rgba(0,0,0,.25)';
    document.body.appendChild(t);
    fetch(API,{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+TOKEN},body:JSON.stringify({html:html,source_url:u,image_url:img})})
      .then(function(r){return r.json().then(function(d){return{ok:r.ok,d:d}})})
      .then(function(x){t.textContent=x.ok?('✓ Added: '+(x.d.title||'Rental')):('Error: '+(x.d.detail&&x.d.detail.message||x.d.detail||'failed'));t.style.background=x.ok?'#4A5D4E':'#C25934';setTimeout(function(){t.remove()},4500)})
      .catch(function(e){t.textContent='Error: '+e.message;t.style.background='#C25934';setTimeout(function(){t.remove()},4500)});
  },0);void(0);`;
  const compact = fn.replace(/\n\s*/g, "").replace(/\s{2,}/g, " ");
  return "javascript:" + encodeURI(compact);
};

export const BookmarkletDialog = ({ open, onOpenChange, targetUrl, targetHost }) => {
  const [copied, setCopied] = useState(false);
  // Computed each render so the embedded session token is always current.
  const href = buildBookmarkletJs(API, getToken());

  // React blocks `javascript:` in href attributes. Set imperatively via callback ref.
  const setLinkRef = (node) => {
    if (node) node.setAttribute("href", href);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      toast.success("Bookmarklet code copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const guided = !!targetUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={RENTAL.bookmarkletDialog}
        className="max-w-xl bg-[#F9F8F6] border-stone-200 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-medium tracking-tight">
            {guided
              ? `${targetHost || "This site"} needs the bookmarklet`
              : "The LeaseDeck Bookmarklet"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            {guided
              ? "It uses Cloudflare bot protection, so we can't fetch it from our server. Use the bookmarklet from your own browser tab instead — one click and the listing is in your dashboard."
              : "One-click capture from any page you're already viewing — works on RentFaster, Zillow, Craigslist, Kijiji, and most rental sites. (Facebook Marketplace blocks all bookmarklets via CSP — use 'Paste ad text' mode for FB.)"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <p className="overline mb-3">Step 1 — Install (once)</p>
            <p className="text-sm text-stone-700 mb-4 leading-relaxed">
              Show your bookmarks bar{" "}
              <span className="text-stone-500">(Cmd/Ctrl + Shift + B)</span>,
              then drag the green button below onto it.
            </p>
            <div className="flex items-center justify-center py-3">
              <a
                ref={setLinkRef}
                data-testid={RENTAL.bookmarkletLink}
                onClick={(e) => {
                  e.preventDefault();
                  toast.info("Drag this button to your bookmarks bar (don't click)", {
                    duration: 4000,
                  });
                }}
                draggable
                className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6] rounded-full px-6 py-3 font-medium text-sm cursor-grab active:cursor-grabbing select-none shadow-sm"
              >
                <Bookmark size={16} strokeWidth={1.8} />
                Add to LeaseDeck
              </a>
            </div>
            <div className="mt-3 flex items-center justify-center">
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 transition-colors"
              >
                {copied ? (
                  <>
                    <Check size={12} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> or copy the code manually
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <p className="overline mb-3">Step 2 — Capture {guided ? "this listing" : "any listing"}</p>
            {guided ? (
              <div className="space-y-3">
                <a
                  href={targetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-[#F9F8F6] rounded-2xl px-5 py-3 font-medium text-sm transition-colors w-full justify-center"
                >
                  <ExternalLink size={15} strokeWidth={1.8} />
                  Open listing in new tab
                </a>
                <ol className="text-sm text-stone-700 space-y-2 list-decimal list-inside pt-1">
                  <li>Click the button above to open the listing</li>
                  <li>
                    On that tab, click your{" "}
                    <span className="inline-flex items-center gap-1 bg-[#4A5D4E] text-[#F9F8F6] rounded px-2 py-0.5 text-xs">
                      <Bookmark size={11} /> Add to LeaseDeck
                    </span>{" "}
                    bookmark
                  </li>
                  <li>You'll see a green confirmation toast on the page</li>
                  <li>Come back here — the rental will be in your list</li>
                </ol>
                <p className="text-xs text-stone-500 pt-2 leading-relaxed">
                  For Facebook Marketplace: make sure you're logged into Facebook
                  on that tab first.
                </p>
              </div>
            ) : (
              <ol className="text-sm text-stone-700 space-y-2 list-decimal list-inside">
                <li>Open a rental ad in your browser (FB Marketplace, RentFaster, Zillow, etc.)</li>
                <li>
                  Click the{" "}
                  <span className="inline-flex items-center gap-1 bg-[#4A5D4E] text-[#F9F8F6] rounded px-2 py-0.5 text-xs">
                    <Bookmark size={11} /> Add to LeaseDeck
                  </span>{" "}
                  bookmark
                </li>
                <li>A small green toast appears confirming the capture</li>
                <li>Come back here and refresh — your rental is in the list</li>
              </ol>
            )}
          </div>

          <div className="bg-stone-100/60 border border-stone-200 rounded-2xl p-4 text-xs text-stone-600 leading-relaxed">
            <p className="font-medium text-stone-700 mb-1">How it works</p>
            The bookmarklet captures the page's HTML from{" "}
            <em>your logged-in browser tab</em>, sends it to our server, and our
            AI extracts the structured rental data. Nothing is captured unless
            you click it. Works on any site you can view yourself.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookmarkletDialog;
